import { logger, logoutEvent } from "@platform/browser-logger";

import { refreshPasswordSession, revokePasswordSession } from "./session-api";
import { claimsFromSession } from "./session-claims";
import {
  refreshPathFor,
  type SessionEndReason,
  signedOutReturnUrl,
} from "./session-rules";
import {
  buildStoredSession,
  clearSession,
  readSession,
  removeSession,
  type StoredSession,
  storeSession,
} from "./session-storage";
import {
  getSsoAccessToken,
  logOutOfSso,
  revokeSsoRefreshToken,
  type SsoConfig,
} from "./sso-client";

export interface SessionConfig {
  /** The backend API's base URL, ending in a slash. */
  readonly apiBaseUrl: string;
  readonly sso: SsoConfig;
}

export interface SessionActions {
  getAccessToken(): Promise<string | undefined>;
  endSession(reason: SessionEndReason): Promise<void>;
}

const describeError = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// A refresh can outlive its session: the user may log out, or log in as someone
// else, while it is in flight.
const isStillCurrent = (session: StoredSession): boolean =>
  readSession()?.accessToken === session.accessToken;

// A failed revoke still ends the session locally; the refresh token just stays valid
// at Auth0 until it expires. The log names the user explicitly, since the session's
// Datadog user is cleared before the revoke settles.
const revokeRefreshToken = async (
  config: SessionConfig,
  session: StoredSession | undefined,
): Promise<void> => {
  if (!session) return;

  try {
    if (session.provider === "sso") {
      await revokeSsoRefreshToken(config.sso);
    } else if (session.refreshToken) {
      await revokePasswordSession(config.apiBaseUrl, session.refreshToken);
    }
  } catch (error: unknown) {
    logger.warn("Revoking the session's refresh token failed", {
      error: describeError(error),
      sub: claimsFromSession(session).sub,
    });
  }
};

export interface SessionCallbacks {
  /** Called as this tab starts ending the session, before anything is cleared. */
  readonly onSessionEnding: () => void;
  readonly onSessionEnd: (reason: SessionEndReason) => void;
}

export const createSessionActions = (
  config: SessionConfig,
  { onSessionEnding, onSessionEnd }: SessionCallbacks,
): SessionActions => {
  let refreshInFlight: Promise<string | undefined> | undefined;

  const endSession = async (reason: SessionEndReason): Promise<void> => {
    onSessionEnding();
    const session = readSession();
    // Signed out here, before the revoke, so a slow or unreachable revoke can't hold
    // up logout. The revoke uses the tokens read above.
    removeSession();
    logoutEvent();
    onSessionEnd(reason);
    await revokeRefreshToken(config, session);
    clearSession();
    if (session?.provider === "sso") {
      // Redirects through Auth0, so it goes last.
      await logOutOfSso(
        config.sso,
        signedOutReturnUrl(window.location.origin, reason),
      );
    }
  };

  const refreshPassword = async (
    session: StoredSession,
    refreshToken: string,
  ): Promise<string | undefined> => {
    const tokens = await refreshPasswordSession(
      config.apiBaseUrl,
      refreshToken,
    );
    if (!isStillCurrent(session)) return readSession()?.accessToken;

    storeSession(
      buildStoredSession(
        {
          ...tokens,
          idToken: tokens.idToken ?? session.idToken,
          refreshToken: tokens.refreshToken ?? refreshToken,
        },
        { keepSignedIn: session.keepSignedIn, provider: "password" },
      ),
    );
    return tokens.accessToken;
  };

  const refreshSso = async (
    session: StoredSession,
  ): Promise<string | undefined> => {
    const accessToken = await getSsoAccessToken(config.sso);
    if (!isStillCurrent(session)) return readSession()?.accessToken;

    storeSession({ ...session, accessToken });
    return accessToken;
  };

  const refresh = async (
    session: StoredSession,
  ): Promise<string | undefined> => {
    try {
      const path = refreshPathFor(session, Date.now());
      if (path === "sso") return await refreshSso(session);
      if (path === "password" && session.refreshToken) {
        return await refreshPassword(session, session.refreshToken);
      }
    } catch (error: unknown) {
      logger.warn("Refreshing the session failed", {
        error: describeError(error),
      });
    }
    if (isStillCurrent(session)) await endSession("expired");
    return undefined;
  };

  const getAccessToken = async (): Promise<string | undefined> => {
    const session = readSession();
    if (!session) return undefined;
    if (refreshPathFor(session, Date.now()) === "current") {
      return session.accessToken;
    }

    // Every request waiting on the same expired token shares one refresh.
    refreshInFlight ??= refresh(session).finally(() => {
      refreshInFlight = undefined;
    });
    return refreshInFlight;
  };

  return { getAccessToken, endSession };
};
